import { useEffect, useMemo } from 'react';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import usePersistedKeyValueStore from '../../../../../hooks/usePersistedKeyValueStore';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import urls from '../../../../../utils/urls';
import { SelectableProfileListItem } from '../../../../shared/LogList/types';

export type UseSelectedProfileIdsReturnValue = {
    selectedProfileIds: string[];
    onToggleProfileId: (profileId: string) => void;
    onSetProfileIds: (profileIds: string[]) => void;
    profiles: SelectableProfileListItem[];
    isLoadingProfiles: boolean;
};

const defaultValue: string[] = [];

export const useSelectedProfileIds = (): UseSelectedProfileIdsReturnValue => {
    const loadedUser = useLoadedUser({});
    const persistedStore = usePersistedKeyValueStore<string[]>({
        category: 'homebase-selectedProfileIds',
        key: loadedUser?.user?.userId || '',
        defaultValue,
        areValuesEqual: (newValue: string[], existing: string[] | undefined) => {
            if (!existing) return false;

            return newValue.length === existing.length && newValue.every((value) => existing.includes(value));
        },
    });
    const { medicalProfiles, collaboratorMedicalProfiles, isLoadingProfiles } = useMedicalProfiles();
    const allProfiles = useMemo<SelectableProfileListItem[]>(
        () =>
            [...(medicalProfiles || []), ...(collaboratorMedicalProfiles || [])].map((profile) => ({
                ...profile,
                avatarUrl: urls.api.medicalProfiles.avatar(profile.medicalProfileId),
            })),
        [medicalProfiles, collaboratorMedicalProfiles],
    );

    // invalidate persisted store selections if profiles don't exist (maybe deleted) or set all profiles if none are selected
    useEffect(() => {
        if (!isLoadingProfiles) {
            const validProfileIds = persistedStore.value.filter((profileId) =>
                allProfiles.find((profile) => profile.medicalProfileId === profileId),
            );
            if (validProfileIds.length !== persistedStore.value.length) {
                persistedStore.setValue(validProfileIds);
            }

            // if finished loading and nothing is selected, select all profiles
            if (persistedStore.value.length === 0) {
                persistedStore.setValue(allProfiles.map((profile) => profile.medicalProfileId));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allProfiles, isLoadingProfiles]);

    const onToggleProfileId = (profileId: string) => {
        if (persistedStore.value.includes(profileId)) {
            persistedStore.setValue(persistedStore.value.filter((id) => id !== profileId));
        } else {
            persistedStore.setValue([...persistedStore.value, profileId]);
        }
    };

    const validatedSelectedProfileIds = useMemo(
        () =>
            allProfiles
                .map((profile) => profile.medicalProfileId)
                .filter((profileId) => {
                    return (persistedStore.value || []).includes(profileId);
                }),
        [allProfiles, persistedStore.value],
    );

    return {
        selectedProfileIds: validatedSelectedProfileIds,
        onToggleProfileId,
        profiles: allProfiles,
        isLoadingProfiles: isLoadingProfiles || persistedStore.isLoading,
        onSetProfileIds: persistedStore.setValue,
    };
};
